import { KeyboardNavigator } from '@bn-games/input-core';
import { DEFAULT_BOARD_SIZE, BOARD_INLAY_OFFSET, SQUARE_SIZE } from '../utils/constants.js';
import type { Square } from '../game/types.js';
import type { ViewerRotation } from '../render/geometry.js';

export interface KeyboardPlayCallbacks {
  /** Synthetic hover at the cursor — feeds MoveSelector hover-preview pipeline. */
  readonly hoverAt: (x: number, y: number) => void;
  /** Synthetic click (down + up) at the cursor — selects, steps, commits. */
  readonly clickAt: (x: number, y: number) => void;
  /** Cancel an active selection (mid-`selected`, not mid-`chaining`). */
  readonly cancelSelection: () => void;
  /** Schedule a render — the focus ring lives in the canvas paint loop. */
  readonly requestRender: () => void;
  /** Current viewer rotation (0 or 180). */
  readonly getRotation: () => ViewerRotation;
  /** Cursor moved to a new board square — caller narrates it for the SR. */
  readonly onCursorMove: (square: Square) => void;
}

/**
 * Owns the 8×8 KeyboardNavigator and the Enter / Escape hotkeys for checkers.
 * Renderer-agnostic — emits synthetic pointer coordinates so MoveSelector's
 * existing hover / click pipeline does the work.
 *
 * The cursor lives in **visual (rotated) coordinates**: index 0 is the
 * top-left square as the local player sees it. That keeps Arrow Up = "row
 * toward the top of the screen" regardless of which side the player sits on.
 * Square names announced by the narrator stay anchored to White's view via
 * algebraic notation — the visual cursor just controls which canvas pixel the
 * synthetic pointer hits.
 */
export class KeyboardPlay {
  private readonly nav: KeyboardNavigator;
  private readonly callbacks: KeyboardPlayCallbacks;
  private focused = false;

  constructor(callbacks: KeyboardPlayCallbacks) {
    this.callbacks = callbacks;
    this.nav = new KeyboardNavigator({
      layout: { mode: 'grid', cols: DEFAULT_BOARD_SIZE, rows: DEFAULT_BOARD_SIZE },
      // Sensible starting square — visual row 6 (one row above bottom in
      // White's view) feels like a typical opening cursor, and stays on a
      // dark square in both rotations.
      initialIndex: 6 * DEFAULT_BOARD_SIZE + 4,
    });
    this.nav.onFocusChange(() => {
      const { x, y } = this.getCursorCoords();
      this.callbacks.hoverAt(x, y);
      this.callbacks.onCursorMove(this.getCursorSquare());
      this.callbacks.requestRender();
    });
  }

  setFocused(focused: boolean): void {
    if (this.focused === focused) return;
    this.focused = focused;
    this.callbacks.requestRender();
  }

  isFocused(): boolean {
    return this.focused;
  }

  /** Board square under the cursor, accounting for current rotation. */
  getCursorSquare(): Square {
    const idx = this.nav.getIndex();
    const visualRow = Math.floor(idx / DEFAULT_BOARD_SIZE);
    const visualCol = idx % DEFAULT_BOARD_SIZE;
    if (this.callbacks.getRotation() === 0) {
      return { row: visualRow, col: visualCol };
    }
    return {
      row: DEFAULT_BOARD_SIZE - 1 - visualRow,
      col: DEFAULT_BOARD_SIZE - 1 - visualCol,
    };
  }

  /**
   * Handle a keydown. Returns true if the key was consumed (caller should
   * preventDefault). Other keys fall through to the rest of the page.
   */
  handleKey(key: string): boolean {
    if (this.nav.handleKey(key)) return true;

    if (key === 'Enter' || key === ' ') {
      const { x, y } = this.getCursorCoords();
      this.callbacks.clickAt(x, y);
      return true;
    }

    // Escape on the canvas surfaces "cancel an active selection" first;
    // the gear-overlay toggle is owned by main.ts's capture-phase listener
    // and triggers on the document-level keydown (capture-phase stop
    // prevents the canvas listener from ever seeing Escape). So we never
    // observe Escape here — but if the capture-phase ordering ever
    // changes, returning false keeps the document handler in charge.
    return false;
  }

  /** Map the current cursor to canvas drawing-space coords (matches pointer geometry). */
  private getCursorCoords(): { x: number; y: number } {
    const idx = this.nav.getIndex();
    const visualRow = Math.floor(idx / DEFAULT_BOARD_SIZE);
    const visualCol = idx % DEFAULT_BOARD_SIZE;
    return {
      x: BOARD_INLAY_OFFSET + visualCol * SQUARE_SIZE + SQUARE_SIZE / 2,
      y: BOARD_INLAY_OFFSET + visualRow * SQUARE_SIZE + SQUARE_SIZE / 2,
    };
  }
}
